import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { TypographyProps } from "@mui/material/Typography";
import { useMemo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import {
  selectHandRaiseStartTime,
  selectWorkstationStatus,
  selectHandRaisedForWorkstation
} from "~/features/andon/andon.selectors";
import {
  selectPortOpenByIdMap,
  selectGridModeByIdMap
} from "~/features/andon/andon.slice";
import {
  figureAndonColorFromStatus,
  getWorkstationModeIcon
} from "~/features/andon/andon.utils";

import { getWorkstationModeDisplayName } from "~/lib/workstations.logic";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";
import { WorkstationDto } from "~/types/api";

type AndonBoardElementProps = {
  workstationId: string;
  variant: TypographyProps["variant"];
  isCompact: boolean;
};

export function AndonBoardElement(props: AndonBoardElementProps) {
  const { workstationId, isCompact, variant } = props;

  const { t } = useTranslation();

  const { data: workstations } = useGetWorkstationsQuery();

  const workstationStatus = useAppSelector(
    selectWorkstationStatus(workstationId)
  );
  const portOpenById = useAppSelector(selectPortOpenByIdMap);
  const gridSystemModeById = useAppSelector(selectGridModeByIdMap);

  const handRaiseStart = useAppSelector(
    selectHandRaiseStartTime(workstationId)
  );

  const [elapsedTime, setElapsedTime] = useState("");

  useEffect(() => {
    if (!handRaiseStart) {
      setElapsedTime("0s");
      return;
    }

    const update = () => {
      const elapsedMs = Date.now() - new Date(handRaiseStart).getTime();
      const totalSec = Math.floor(elapsedMs / 1000);

      const hours = Math.floor(totalSec / 3600);
      const mins = Math.floor((totalSec % 3600) / 60);
      const secs = totalSec % 60;

      const hoursPart = hours > 0 ? `${hours}h` : "";
      const minsPart = hours > 0 || mins > 0 ? `${mins}m` : "";
      const secsPart = `${secs}s`;

      const formatted = [hoursPart, minsPart, secsPart]
        .filter(Boolean)
        .join(" ");
      setElapsedTime(formatted);
    };

    update();

    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [handRaiseStart]);

  const ports = useMemo(() => {
    const ws = workstations?.find((w) => w.id === workstationId);
    return ws?.ports ?? [];
  }, [workstations, workstationId]);

  const workstation = workstations?.find((w) => w.id === workstationId);

  const gridSystemMode = workstation
    ? (gridSystemModeById[workstation.autostoreGridId] ?? null)
    : undefined;

  const isOpen = ports.some((p) => portOpenById[p.portId.toString()]);
  const handRaised = useAppSelector(
    selectHandRaisedForWorkstation(workstationId)
  );
  if (!workstation) return null;

  const shouldShowModeIcon = workstation.status === "Active" || isOpen;

  const {
    backgroundColor,
    borderColor,
    textColor,
    icon: StatusIcon,
    altTextKey
  } = figureAndonColorFromStatus({
    gridSystemMode: gridSystemMode ?? undefined,
    workstationStatus,
    workstationActive: workstation.status === "Active",
    workstationOpen: isOpen,
    handRaised: handRaised.raised
  });

  // Decide what label to show: either translated altTextKey, or fallback to workstation.mode
  const translatedLabel = altTextKey ? t(altTextKey) : null;
  // Get the canonical display label for this workstation's mode
  const displayMode = getWorkstationModeDisplayName(workstation.mode);
  const componentLabel = translatedLabel || displayMode;
  const ModeIcon =
    shouldShowModeIcon && workstation.mode
      ? getWorkstationModeIcon(workstation.mode as WorkstationDto["mode"])
      : null;

  return (
    <Box
      key={workstation.id}
      aria-label={workstation.deviceId}
      sx={{
        backgroundColor: backgroundColor,
        border: `1px solid ${borderColor}`,
        boxSizing: "border-box",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
        height: "100%",
        justifyContent: "space-between"
      }}
    >
      {/* top third */}
      <Box
        sx={{
          width: "100%",
          height: "66%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        {/* icon */}
        {!isCompact && (
          <Box
            sx={{
              width: "100%",
              height: "75%", // icon row takes up 75% of the top third
              display: "flex",
              alignItems: "flex-end", // align to bottom
              justifyContent: "center",
              overflow: "hidden",
              pb: 1
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: isCompact ? undefined : -2
              }}
            >
              {/* status icon */}
              {StatusIcon && (
                <StatusIcon
                  className="andon-icon"
                  fill={textColor}
                  color={textColor}
                  width="100%"
                  height="60%" // control relative icon size here
                />
              )}
              {/* mode icon */}
              {!StatusIcon && ModeIcon && (
                <ModeIcon
                  className="andon-icon"
                  fill={textColor}
                  color={textColor}
                  width="100%"
                  height="60%" // control relative icon size here
                />
              )}
            </Box>
          </Box>
        )}
        {/* workstation mode */}
        <Box
          sx={{
            height: "25%", // mode label takes up 25% of the top third
            display: "flex",
            alignItems: "flex-start", // align to top
            justifyContent: "center",
            minHeight: 0,
            mt: 1
          }}
        >
          <Typography
            variant={variant}
            color={textColor}
            fontWeight="400"
            data-testid={handRaised.raised ? "hand-raise-timer" : undefined}
            sx={{ lineHeight: 1, cursor: "default" }}
          >
            {handRaised.raised && elapsedTime ? elapsedTime : componentLabel}
          </Typography>
        </Box>
      </Box>

      {/* bottom third */}
      {/* workstation name */}
      <Box
        sx={{
          width: "100%",
          height: "34%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          boxSizing: "border-box",
          px: 1
        }}
      >
        <Typography
          variant={variant}
          color={textColor}
          noWrap
          fontWeight="400"
          sx={{ cursor: "default" }}
        >
          {workstation.deviceId}
        </Typography>
      </Box>
    </Box>
  );
}

export default AndonBoardElement;
